import { call, put } from 'redux-saga/effects';

import { stopLoading, hideModal } from 'js/actionCreators/uiActions';
import { DRAW_TYPE_REVEAL } from 'js/config/animationOptions';
import { FILE_CONTENT_TYPES, IMAGE_PROVIDER_TYPES } from 'js/config/consts';
import uploadingAssetProvider from 'js/shared/providers/UploadingAssetProvider';
import { BlendMode, DrawType } from 'js/types';
import { createImageElement } from 'js/actionCreators/scribeActions';

import { compareSvgToConverted, PIXEL_DIFFERENCE_ALLOWANCE } from '../compareSvgToConverted';
import getCanvasAttributeFromFile from '../sagaHelpers/getCanvasAttributeFromFile';
import { processImageFileOnLoad } from '../sagaHelpers/processImageFileOnLoad';

interface processImageAndCreateElementOptions {
  assetId: number;
  canvasSize: { width: number; height: number };
  elementId: string;
  filename: string;
  originalFile: Blob;
  provider: string;
  scribeId: number;
  isPremium: boolean;
}

export function* processImageAndCreateElement({
  assetId,
  canvasSize,
  elementId,
  filename,
  originalFile,
  provider,
  scribeId,
  isPremium
}: processImageAndCreateElementOptions) {
  const {
    file: processedFile,
    viewboxScribeElementProperties,
    recolorScribeElementProperties,
    containsEmbeddedImage
  } = yield call(processImageFileOnLoad, {
    file: originalFile
  });

  const { animationTime, scaleX, scaleY, x, y, width, height, _imageUrl } = yield call(getCanvasAttributeFromFile, {
    file: processedFile,
    canvasSize: canvasSize
  });

  uploadingAssetProvider.save(assetId, _imageUrl);

  let drawTypeOverride;
  if (originalFile.type === FILE_CONTENT_TYPES.SVG) {
    // If image is a svg - compare the processedFile with the output of SVG converstion for Pixi to see what the pixel diff is
    // if its above the threshold, change the animation type to reveal draw

    try {
      const pixelDifference = (yield call(compareSvgToConverted, processedFile)) as Awaited<
        ReturnType<typeof compareSvgToConverted>
      >;
      if (pixelDifference > PIXEL_DIFFERENCE_ALLOWANCE) {
        drawTypeOverride = DRAW_TYPE_REVEAL as DrawType;
      }
    } catch (error) {
      console.error('Error comparing SVG to converted version:', error);
    }
  }
  let blendModeProps = {};
  if (originalFile.type === FILE_CONTENT_TYPES.GIF) {
    blendModeProps = { blendMode: BlendMode.Multiply };
  }

  yield put(
    createImageElement({
      scribeId,
      drawTypeOverride,
      initialData: {
        id: elementId,
        image: {
          contentType: originalFile.type,
          provider,
          filename: filename,
          assetId: assetId
        },
        scaleX,
        scaleY,
        x,
        y,
        width,
        height,
        _imageUrl,
        _imageLoaded: provider === IMAGE_PROVIDER_TYPES.USER || provider === IMAGE_PROVIDER_TYPES.AI_LIBRARY,
        _imageLoading: ['library', 'noun-project', 'upload'].includes(provider),
        animationTime,
        containsEmbeddedImage,
        ...viewboxScribeElementProperties,
        ...recolorScribeElementProperties,
        ...blendModeProps,
        isPremium
      }
    })
  );

  yield put(stopLoading());
  yield put(hideModal(scribeId));
}
